import React, { useState, useRef } from "react";
import { DragDropContext } from "@hello-pangea/dnd";
import RDVLead from "./RDVLead";
import RdvLM from "./RdvLM";
import ContactLead from "./ContactLead";
import DocumentsTable from "./LMLead";
import PropLM from "./PropositionLMLead";
import ArchiveLead from "./ArchiveLead";
import DragTransitionOverlay from "./DragTransitionOverlay";
import DragDocTransitionOverlay from "./DragDocTransitionOverlay";
import { IoIosSearch } from "react-icons/io";

import { useEtatGlobal } from "../EtatGlobal.js";
import { authFetch } from "../../HttpClient";
import { uploadFileToBlob } from "../../utils/uploadFileToBlob";

import { Archive, Plus, GripVertical } from "lucide-react";

import FormLead from "./FormLead";

function AllLead() {
  const { leads, loadingLeads, fetchLeads, documents, fetchDocumentsLM, users } =
    useEtatGlobal();
  const [showArchives, setShowArchives] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showFromLeadOverlay, setShowFromLeadOverlay] = React.useState(false);

  const [isDraggingActive, setIsDraggingActive] = useState(false);
  const [dragTransition, setDragTransition] = useState(null);
  const [loadingLeadIds, setLoadingLeadIds] = useState(new Set());
  const [pendingOverrides, setPendingOverrides] = useState({});

  const [isDraggingDocActive, setIsDraggingDocActive] = useState(false);
  const [docTransition, setDocTransition] = useState(null);
  const [loadingDocIds, setLoadingDocIds] = useState(new Set());
  const [pendingDocOverrides, setPendingDocOverrides] = useState({});

  const overlayRef = useRef(null);
  const inoverlayRef = useRef(null);

  const middleColumnStyle = {
    height: "100%",
  };

  // Merge lead data with optimistic overrides
  const mergedLeads = leads.map((lead) =>
    pendingOverrides[lead.id] ? { ...lead, ...pendingOverrides[lead.id] } : lead
  );

  // Applique instantanément une modification de formulaire sur la carte, avant même la réponse de l'API
  const handleLeadOptimisticUpdate = (leadId, payload) => {
    setPendingOverrides((prev) => ({
      ...prev,
      [leadId]: { ...prev[leadId], ...payload },
    }));
  };

  // Retire l'override une fois la sauvegarde réelle terminée (succès ou échec)
  const handleLeadOptimisticClear = (leadId) => {
    setPendingOverrides((prev) => {
      const n = { ...prev };
      delete n[leadId];
      return n;
    });
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleDragStart = () => {
    setIsDraggingActive(true);
  };

  const handleDragEnd = (result) => {
    setIsDraggingActive(false);
    const { source, destination, draggableId } = result;
    if (!destination || source.droppableId === destination.droppableId) return;

    // Use leads (not mergedLeads) to get the original unmodified data
    const lead = leads.find((l) => String(l.id) === draggableId);
    if (!lead) return;

    const tomorrow = new Date(Date.now() + 86400000)
      .toISOString()
      .split("T")[0];
    const yesterday = new Date(Date.now() - 86400000)
      .toISOString()
      .split("T")[0];

    // Placeholder override to visually place the card in destination column immediately
    let placeholderOverride = {};
    if (destination.droppableId === "contact") {
      placeholderOverride = {
        specificite: "contact",
        dateRDV: "",
        heureRDV: "",
        lieuRDV: "",
      };
    } else if (destination.droppableId === "rdv_futur") {
      placeholderOverride = { specificite: "rdv", dateRDV: tomorrow };
    } else if (destination.droppableId === "rdv_passe") {
      placeholderOverride = { specificite: "rdv", dateRDV: yesterday };
    }

    // Move card immediately in the UI
    setPendingOverrides((prev) => ({
      ...prev,
      [lead.id]: placeholderOverride,
    }));

    setDragTransition({
      lead,
      from: source.droppableId,
      to: destination.droppableId,
    });
  };

  const handleTransitionConfirm = async (extraData) => {
    const { lead, from, to } = dragTransition;
    setDragTransition(null);

    const yesterday = new Date(Date.now() - 86400000)
      .toISOString()
      .split("T")[0];

    let payload = {};
    if (to === "contact") {
      payload = {
        specificite: "contact",
        dateRDV: "",
        heureRDV: "",
        lieuRDV: "",
      };
    } else if (
      to === "rdv_passe" &&
      from === "rdv_futur" &&
      !extraData.dateRDV
    ) {
      // rdv_futur → rdv_passe sans formulaire : date à hier
      payload = {
        specificite: "rdv",
        dateRDV: yesterday,
        heureRDV: lead.heureRDV || "",
        lieuRDV: lead.lieuRDV || "",
      };
    } else {
      payload = { specificite: "rdv", ...extraData };
    }

    // Update override with real confirmed data + show loading spinner on card
    setPendingOverrides((prev) => ({ ...prev, [lead.id]: payload }));
    setLoadingLeadIds((prev) => new Set([...prev, lead.id]));

    try {
      await authFetch(
        `https://backend-myalfa.vercel.app/api/formleads/${lead.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      fetchLeads();
    } catch (err) {
      console.error("Erreur déplacement lead:", err);
      // Revert on API failure
      setPendingOverrides((prev) => {
        const n = { ...prev };
        delete n[lead.id];
        return n;
      });
    } finally {
      setLoadingLeadIds((prev) => {
        const n = new Set(prev);
        n.delete(lead.id);
        return n;
      });
      setPendingOverrides((prev) => {
        const n = { ...prev };
        delete n[lead.id];
        return n;
      });
    }
  };

  const handleTransitionCancel = () => {
    const { lead } = dragTransition;
    // Revert: remove override → card returns to its original column
    setPendingOverrides((prev) => {
      const n = { ...prev };
      delete n[lead.id];
      return n;
    });
    setDragTransition(null);
  };

  const handleDocDragStart = () => {
    setIsDraggingDocActive(true);
  };

  const handleDocDragEnd = (result) => {
    setIsDraggingDocActive(false);
    const { source, destination, draggableId } = result;
    if (!destination || source.droppableId === destination.droppableId) return;

    const doc = documents.find((d) => String(d.id) === draggableId);
    if (!doc) return;

    let placeholderDocOverride = {};
    if (destination.droppableId === "lm_signee") {
      placeholderDocOverride = {
        specificite: "completed",
        status: "completed",
      };
    } else if (destination.droppableId === "prop_lm") {
      placeholderDocOverride = { specificite: "sent", status: "sent" };
    }

    setPendingDocOverrides((prev) => ({
      ...prev,
      [doc.id]: placeholderDocOverride,
    }));
    setDocTransition({
      doc,
      from: source.droppableId,
      to: destination.droppableId,
    });
  };

  const handleDocTransitionConfirm = (extraData) => {
    const { doc, to } = docTransition;

    // Ferme l'overlay et applique la mise à jour optimiste immédiatement
    setDocTransition(null);
    setPendingDocOverrides((prev) => ({
      ...prev,
      [doc.id]:
        to === "lm_signee"
          ? { specificite: "completed", status: "completed" }
          : { specificite: "sent", status: "sent" },
    }));
    setLoadingDocIds((prev) => new Set([...prev, doc.id]));

    // Appel API en arrière-plan (non-bloquant)
    (async () => {
      try {
        if (to === "lm_signee") {
          // Upload direct navigateur → Blob (contourne la limite 4,5 Mo,
          // 25/09/2026) : plus besoin de tenter une compression PDF côté
          // client pour rester sous cette limite.
          const payload = {};
          if (extraData.dateSignature) payload.dateSignature = extraData.dateSignature;

          if (extraData.signedPdfFile) {
            payload.signedPdfUrl = await uploadFileToBlob(
              extraData.signedPdfFile,
              "https://backend-myalfa.vercel.app/api/envelope/upload-token"
            );
          }

          await authFetch(
            `https://backend-myalfa.vercel.app/api/envelope/${doc.id}/complete-manual`,
            {
              method: "PUT",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(payload),
            }
          );
        } else {
          await authFetch(
            `https://backend-myalfa.vercel.app/api/envelope/${doc.id}/reopen`,
            {
              method: "PUT",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({}),
            }
          );
        }
        fetchDocumentsLM();
      } catch (err) {
        console.error("Erreur transition document:", err);
        setPendingDocOverrides((prev) => {
          const n = { ...prev };
          delete n[doc.id];
          return n;
        });
      } finally {
        setLoadingDocIds((prev) => {
          const n = new Set(prev);
          n.delete(doc.id);
          return n;
        });
        setPendingDocOverrides((prev) => {
          const n = { ...prev };
          delete n[doc.id];
          return n;
        });
      }
    })();
  };

  const handleDocTransitionCancel = () => {
    const { doc } = docTransition;
    setPendingDocOverrides((prev) => {
      const n = { ...prev };
      delete n[doc.id];
      return n;
    });
    setDocTransition(null);
  };

  return (
    <div>
      <div className="mb-5">
        <h1 className="text-xl font-semibold text-gray-900 m-0">
          Suivi des Lead et Lettres de mission
        </h1>
        <p className="text-xs text-gray-400 mt-1 flex items-center gap-2 flex-wrap">
          Gestion des prospects et lettres de mission
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-400 text-[10px] font-medium select-none">
            <GripVertical className="w-3 h-3" />
            Glisser-déposer pour déplacer les cartes du Lead si besion
            d'ajustement
          </span>
        </p>
      </div>

      <div className="flex items-center justify-between gap-4 mb-5">
        <div className="relative flex-1 max-w-xs">
          <IoIosSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher un prospect..."
            value={searchTerm}
            onChange={handleSearchChange}
            className="pl-8 h-8 w-full rounded-full bg-white border border-gray-200 text-xs focus:outline-none focus:border-gray-300"
          />
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowArchives(!showArchives)}
            className={`flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs font-medium transition-colors ${
              showArchives
                ? "bg-gray-100 text-gray-700 border border-gray-200"
                : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"
            }`}
          >
            <Archive className="w-3 h-3" />
            {showArchives ? "Retour" : "Archive"}
          </button>
          <button
            onClick={() => setShowFromLeadOverlay(true)}
            className="flex items-center gap-1.5 h-8 px-4 rounded-lg text-xs font-semibold text-white bg-[#840040] hover:bg-[#6c0034] transition-colors"
          >
            <Plus className="w-3 h-3" />
            Ajouter un Lead
          </button>
        </div>
      </div>

      {/* Affichage conditionnel */}
      {showArchives ? (
        <div>
          <ArchiveLead
            leadData={leads}
            loading={loadingLeads}
            onUpdate={fetchLeads}
            searchTerm={searchTerm}
          />
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr",
            gap: "10px",
            flex: 1,
            width: "99%",
          }}
        >
          {/* DnD cols 1-3 : leads */}
          <DragDropContext
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <>
              <div
                className={`transition-all duration-200 ${
                  isDraggingDocActive
                    ? "opacity-40 blur-[2px] pointer-events-none select-none"
                    : ""
                }`}
              >
                <ContactLead
                  leadData={mergedLeads}
                  loading={loadingLeads}
                  onUpdate={fetchLeads}
                  searchTerm={searchTerm}
                  columnId="contact"
                  loadingLeadIds={loadingLeadIds}
                  onOptimisticUpdate={handleLeadOptimisticUpdate}
                  onOptimisticClear={handleLeadOptimisticClear}
                />
              </div>
              <div
                style={middleColumnStyle}
                className={`transition-all duration-200 ${
                  isDraggingDocActive
                    ? "opacity-40 blur-[2px] pointer-events-none select-none"
                    : ""
                }`}
              >
                <RDVLead
                  leadData={mergedLeads}
                  loading={loadingLeads}
                  onUpdate={fetchLeads}
                  searchTerm={searchTerm}
                  columnId="rdv_futur"
                  loadingLeadIds={loadingLeadIds}
                  onOptimisticUpdate={handleLeadOptimisticUpdate}
                  onOptimisticClear={handleLeadOptimisticClear}
                />
              </div>
              <div
                style={middleColumnStyle}
                className={`transition-all duration-200 ${
                  isDraggingDocActive
                    ? "opacity-40 blur-[2px] pointer-events-none select-none"
                    : ""
                }`}
              >
                <RdvLM
                  leadData={mergedLeads}
                  loading={loadingLeads}
                  onUpdate={fetchLeads}
                  searchTerm={searchTerm}
                  columnId="rdv_passe"
                  loadingLeadIds={loadingLeadIds}
                  onOptimisticUpdate={handleLeadOptimisticUpdate}
                  onOptimisticClear={handleLeadOptimisticClear}
                />
              </div>
            </>
          </DragDropContext>

          {/* DnD cols 4-5 : documents LM */}
          <DragDropContext
            onDragStart={handleDocDragStart}
            onDragEnd={handleDocDragEnd}
          >
            <>
              {/* Flou si drag lead actif */}
              <div
                style={middleColumnStyle}
                className={`transition-all duration-200 ${
                  isDraggingActive
                    ? "opacity-40 blur-[2px] pointer-events-none select-none"
                    : ""
                }`}
              >
                <PropLM
                  searchTerm={searchTerm}
                  columnId="prop_lm"
                  loadingDocIds={loadingDocIds}
                  docOverrides={pendingDocOverrides}
                />
              </div>
              <div
                className={`transition-all duration-200 ${
                  isDraggingActive
                    ? "opacity-40 blur-[2px] pointer-events-none select-none"
                    : ""
                }`}
              >
                <DocumentsTable
                  searchTerm={searchTerm}
                  columnId="lm_signee"
                  loadingDocIds={loadingDocIds}
                  docOverrides={pendingDocOverrides}
                />
              </div>
            </>
          </DragDropContext>
        </div>
      )}

      {dragTransition && (
        <DragTransitionOverlay
          transition={dragTransition}
          users={users}
          onConfirm={handleTransitionConfirm}
          onCancel={handleTransitionCancel}
        />
      )}

      {docTransition && (
        <DragDocTransitionOverlay
          transition={docTransition}
          onConfirm={handleDocTransitionConfirm}
          onCancel={handleDocTransitionCancel}
        />
      )}

      {showFromLeadOverlay && (
        <div className="overlayAC" ref={overlayRef}>
          <div className="inoverlayMS" ref={inoverlayRef}>
            <FormLead onCancel={() => setShowFromLeadOverlay(false)} />
          </div>
        </div>
      )}
      <br />
      <br />
      <br />
    </div>
  );
}

export default AllLead;
